import { AspectRatio } from '@ironhack/design-system2/components/aspect-ratio';
import { Box, type BoxProps } from '@ironhack/design-system2/components/box';
import { type LinkProps } from '@ironhack/design-system2/components/link';
import {
  type StackProps,
  VStack,
} from '@ironhack/design-system2/components/stack';
import { Text, type TextProps } from '@ironhack/design-system2/components/text';
import { backgroundToTokens } from '@ironhack/design-system2/utils';
import { type ReactElement, type RefObject } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms/structured-text';

import { Button } from '@/app/components/client/button';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { StructuredContent } from '@/app/components/client/structured-content';
import { CmsImage } from '@/app/components/server/cms-image';
import {
  CmsVideo,
  type VideoPlayerData,
} from '@/app/components/server/cms-video';
import {
  type DatoButtonBlock,
  type DatoImage,
  type DatoModuleFormat,
} from '@/lib/datocms/types';
import { type WithTracking } from '@/types';

export type InfoMedia = {
  button?: [WithTracking<DatoButtonBlock> & Pick<LinkProps, 'rel'>];
  content: StructuredTextGraphQlResponse;
  headingProps?: TextProps | null;
  image?: DatoImage;
  mapEmbed?: string;
  mediaSide?: 'left' | 'right';
  moduleFormat: [DatoModuleFormat];
  title: string;
  video?: VideoPlayerData | null;
};

export const getFrameSrc = (frameString: string): string => {
  const frameSrcRegex = /<iframe .*src="(.*?)".*><\/iframe>/u;
  const [, src] = frameString.match(frameSrcRegex) || [];
  return src;
};

type Props = {
  buttonWidth?: string | Array<string | null>;
  data: InfoMedia;
  innerBoxProps?: BoxProps;
  ref?: RefObject<HTMLDivElement>;
  textStackProps?: StackProps;
} & BoxProps;

export const InfoMedia = (props: Props): ReactElement => {
  const {
    buttonWidth,
    data: {
      button: [button] = [],
      content,
      headingProps,
      image,
      mapEmbed,
      mediaSide = 'left',
      moduleFormat: [moduleFormat],
      title,
      video,
    },
    innerBoxProps,
    ref,
    textStackProps,
    title: navTitle,
    ...boxProps
  } = props;

  const { color } = backgroundToTokens(moduleFormat.background);

  const direction =
    mediaSide === 'right'
      ? ['column-reverse' as const, null, 'row-reverse' as const]
      : ['column' as const, null, 'row' as const];

  const frameSrc = mapEmbed && getFrameSrc(mapEmbed);

  return (
    <ModuleWrapper
      innerProps={{
        align: 'center',
        direction,
        gap: [2, null, 5],
        ...innerBoxProps,
      }}
      moduleFormat={moduleFormat}
      outerProps={{
        as: 'section',
        ...boxProps,
      }}
      // Send ref and title directly for NavigationTab
      ref={ref}
      title={navTitle}
    >
      {image || video ? (
        <Box
          as="figure"
          borderRadius="lg"
          overflow="hidden"
          w={[null, null, '50%']}
        >
          {video ? (
            <CmsVideo data={video} />
          ) : (
            image && (
              <CmsImage
                data={image.responsiveImage}
                sizes="(min-width: 768px) 540px, 100vw"
              />
            )
          )}
        </Box>
      ) : (
        frameSrc && (
          <AspectRatio
            as="figure"
            borderRadius="lg"
            overflow="hidden"
            ratio={16 / 9}
            w={['100%', null, '50%']}
          >
            <iframe
              allowFullScreen
              frameBorder="0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={frameSrc}
              style={{ border: 0 }}
              title="campus-location"
            />
          </AspectRatio>
        )
      )}

      <VStack
        align="left"
        spacing="4"
        w={[null, null, '50%']}
        {...textStackProps}
      >
        <VStack align="left" spacing="2">
          <Text as="h2" textStyle={['3xl', null, '4xl']} {...headingProps}>
            {title}
          </Text>

          <StructuredContent color={color} data={content} />
        </VStack>

        {button && (
          <Button w={buttonWidth ?? [null, null, 'full']} {...button} />
        )}
      </VStack>
    </ModuleWrapper>
  );
};
